import React from "react";
import { usePreview } from "@core/hooks/usePreview";
import { getModelHash, ModelData } from "@/data/data";
import { ARButton } from "@components/ARButton";
import { PreviewInteractionHint } from "@components/PreviewInteractionHint";
import HawksbillImage1 from "@assets/turtle-images/hawksbill-1.webp";
import HawksbillVideo from "@assets/turtle-images/hawksbill-turtle-2.mp4";
import HawksbillVideoPoster from "@assets/turtle-images/hawksbill-turtle-2-poster.webp";
import HawksbillImage3 from "@assets/turtle-images/hawksbill-3.webp";
import HawksbillImage4 from "@assets/turtle-images/hawksbill-4.webp";

interface HawksbillSectionProps {
  model: ModelData;
  onLaunchAR: (model: ModelData) => void;
  onTrackLaunchClick?: (
    model: ModelData,
    options?: { transportType?: "beacon" }
  ) => void;
  launchUrl?: string;
  isChecking: boolean;
  isCompatible: boolean;
}

export function HawksbillSection({
  model,
  onLaunchAR,
  onTrackLaunchClick,
  launchUrl,
  isChecking,
  isCompatible,
}: HawksbillSectionProps) {
  const { canvasRef, isFullscreen } = usePreview(model.id);

  const hawksbillCopy = {
    row1: {
      left: {
        title: "Turtle shells shining in the sun",
        image: HawksbillImage1,
        imageAlt: "Hawksbill turtle shell detail",
        imageCaption: "Photo by © WWF-Australia",
        paragraphs: [
          "Once abundant in the tropics, there’s thought to be less than 5,000 female adult hawksbill turtles in the Pacific Ocean – a quarter of historical populations.",
          "The hawksbill has a beautiful shell that’s long been exploited for tortoiseshell jewellery. International trade of tortoiseshell is illegal, but trafficking continues to push hawksbill turtles to near-extinction.",
          "Hawksbills are found in the coral reefs of Hervey Bay, but they are more common further north.",
        ],
      },
      right: {
        title: "Picky eaters: soft but not too hot",
        video: HawksbillVideo,
        poster: HawksbillVideoPoster,
        imageAlt: "Hawksbill turtle feeding on reef",
        imageCaption: "Video by © Breannah Mitchell",
        paragraphs: [
          "Hawksbill turtles use their sharp, pointed beak to pick out sponges and soft corals from reefs.",
          "By eating sponges, hawksbills keep coral reefs healthy and diverse, allowing a good balance between coral and sponges.",
          "But with climate change and increasingly hotter oceans, there are fewer sponges for hawksbills to eat and the coral also bleaches and dies.",
        ],
      },
    },
    row2: {
      left: {
        title: "How hard is a turtle’s shell?",
        image: HawksbillImage3,
        imageAlt: "Hawksbill shell injury awareness image",
        imageCaption: (
          <>
            Photo by{" "}
            <a href="https://commons.wikimedia.org/wiki/File:Hawksbill_turtle_doeppne-081.jpg" target="_blank" rel="noopener noreferrer" className="underline hover:opacity-70">Tom Doeppner</a>,{" "}
            <a href="https://creativecommons.org/licenses/by-sa/3.0/deed.en" target="_blank" rel="noopener noreferrer" className="underline hover:opacity-70">CC BY-SA 3.0</a>
          </>
        ),
        useImageCaptionSpacer: true,
        paragraphs: [
          "Sea turtle shells are not as unbreakable as you might think. A turtle’s shell is living bone with blood vessels and nerves – like our skull – that grows with the turtle.",
          "A single strike from a boat or jet ski could easily fracture a turtle’s shell, puncturing or crushing its lungs that sit just under the shell.",
          "Most turtles die from boat strikes, but shell injuries can also cause internal bleeding, floating syndrome and infection.",
        ],
      },
      right: {
        title: "My face tells a story",
        image: HawksbillImage4,
        imageAlt: "Hawksbill facial scale pattern close-up",
        imageCaption: (
          <>
            Photo by{" "}
            <a href="https://commons.wikimedia.org/wiki/File:Hawksbill_turtle_doeppne-081.jpg" target="_blank" rel="noopener noreferrer" className="underline hover:opacity-70">Tom Doeppner</a>,{" "}
            <a href="https://creativecommons.org/licenses/by-sa/3.0/deed.en" target="_blank" rel="noopener noreferrer" className="underline hover:opacity-70">CC BY-SA 3.0</a>
          </>
        ),
        paragraphs: [
          "Just like a human fingerprint, the scales on every sea turtle’s face are unique.",
          "Researchers are now using facial identification technology (like that used for matching human faces to passports) to better identify and track turtles.",
          "Compared to tagging turtles, facial recognition takes less time, is less invasive and costs less.",
        ],
      },
    },
  };

  const handleLaunchAR = (selectedModel: ModelData) => {
    if (selectedModel.arType === "webxr" && isCompatible) {
      console.log("[View AR / App Clip] launchUrl:", launchUrl);
      onLaunchAR(selectedModel);
    }
  };

  const renderModelViewer = () => (
    <div className="w-full relative h-[300px] sm:h-[400px] md:h-[549px]">
      <canvas
        ref={canvasRef}
        className="absolute inset-0 z-[60]"
        tabIndex={-1}
        style={{ width: "100%", height: "100%", outline: "none" }}
      />
    </div>
  );

  const renderFullscreenUI = () => {
    if (!isFullscreen) return null;
    return (
      <div className="fixed inset-0 z-[1000] pointer-events-none animate-fade-in">
        <div className="absolute top-4 left-4 text-white text-lg font-bold drop-shadow-lg">
          Hawksbill turtle
        </div>
        <PreviewInteractionHint />
      </div>
    );
  };

  const renderSectionBody = (section: {
    title: string;
    image?: string;
    video?: string;
    poster?: string;
    imageAlt: string;
    imageCaption?: React.ReactNode;
    useImageCaptionSpacer?: boolean;
    paragraphs: string[];
  }) => (
    <div className="flex flex-col gap-6 md:gap-[32px] 2xl:gap-[40px]">
      <div className="flex flex-col gap-[10px] md:gap-[12px] 2xl:gap-[14px]">
        <div className="w-full h-[200px] sm:h-[240px] md:h-[265px] overflow-hidden bg-gray-300">
          {section.video ? (
            <video
              src={section.video}
              poster={section.poster}
              className="w-full h-full object-cover"
              playsInline
              controls
              preload="metadata"
            />
          ) : (
            <img
              src={section.image}
              alt={section.imageAlt}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          )}
        </div>
        {section.imageCaption ? (
          <p
            aria-hidden={section.useImageCaptionSpacer || undefined}
            className={`${section.useImageCaptionSpacer ? "hidden md:block md:invisible " : ""}text-black text-[12px] font-light leading-[1.2em] italic`}
          >
            {section.imageCaption}
          </p>
        ) : null}
      </div>
      <h3
        className="text-black text-[20px] sm:text-[24px] 2xl:text-[28px]"
        style={{
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 500,
          lineHeight: "1.2em",
        }}
      >
        {section.title}
      </h3>
      <div className="flex flex-col gap-2 sm:gap-3">
        {section.paragraphs.map((paragraph, i) => (
          <p
            key={i}
            className="text-black text-[14px] sm:text-[16px] 2xl:text-[18px]"
            style={{
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 300,
              lineHeight: "1.2em",
            }}
            dangerouslySetInnerHTML={{ __html: paragraph }}
          />
        ))}
      </div>
    </div>
  );

  return (
    <section
      id={getModelHash(model)}
      className="section-lazy relative overflow-hidden bg-white py-12 px-6 sm:py-16 sm:px-10 md:py-20 md:px-20 2xl:py-[120px] 2xl:px-[155px]"
    >
      {renderFullscreenUI()}

      <div className="flex flex-col w-full max-w-[1130px] mx-auto gap-8 sm:gap-10 md:gap-[60px]">
        <div className="flex flex-col gap-1 sm:gap-2 md:gap-2 2xl:gap-3">
          <h2
            className="text-black text-left font-[600] text-[24px] sm:text-[28px] md:text-[32px] 2xl:text-[40px]"
            style={{
              lineHeight: "1em",
              letterSpacing: "-0.72px",
            }}
          >
            Hawksbill turtle
          </h2>
          <h3 className="leading-[1rem] font-light text-[18px] sm:text-[20px] md:text-[24px] 2xl:text-[30px] italic">
            {model.scientificName}
          </h3>
        </div>

        <div className="flex flex-col gap-6 sm:gap-[30px]">
          <div className="flex flex-col gap-[10px] md:gap-[12px] 2xl:gap-[14px]">
            {renderModelViewer()}
            <span className="text-[12px] leading-[1.2em] invisible">
              &nbsp;
            </span>
          </div>
          <div className="w-full flex justify-center">
            <ARButton
              model={model}
              onLaunchAR={handleLaunchAR}
              onTrackLaunchClick={onTrackLaunchClick}
              isChecking={isChecking}
              isCompatible={isCompatible}
            />
          </div>
        </div>

        <div className="flex flex-col gap-10 sm:gap-[50px]">
          <div className="flex flex-col md:flex-row gap-8 md:gap-[32px] 2xl:gap-[40px]">
            <div className="flex-1">{renderSectionBody(hawksbillCopy.row1.left)}</div>
            <div className="flex-1">{renderSectionBody(hawksbillCopy.row1.right)}</div>
          </div>
          <div className="flex flex-col md:flex-row gap-8 md:gap-[32px] 2xl:gap-[40px]">
            <div className="flex-1">{renderSectionBody(hawksbillCopy.row2.left)}</div>
            <div className="flex-1">{renderSectionBody(hawksbillCopy.row2.right)}</div>
          </div>
        </div>
      </div>
    </section>
  );
}
